import { Grid, Box, Divider } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import {
  Body1,
  Caption,
  H1,
  PrimaryButton,
  SecondaryButton,
  startNextCase,
  authorizeBaseUrl,
  useParsedQueryString,
  useSyncQueryParamsToPath,
  convertHealthPlanEnumToName,
} from "@coherehealth/common";
import DashboardMetrics from "components/DashboardPage/DashboardMetrics";
import { useEffect, useState, useContext, useCallback } from "react";
import { ServiceCase, useGetNextCaseForQueue, useGetServiceCase } from "@coherehealth/qm-api";
import { useSnackbar } from "notistack";
import LoadingDialog from "components/common/modals/LoadingDialog";
import { UserContext } from "UserContext";
import routes from "routes";
import { useGetLettersExpansionConfigurationByPayer } from "hooks/useGetFeatureConfigurations";
import { ContinueToClinicalReviewBanner } from "./PageSections/ContinueToClinicalReviewBanner";
import { useClinicalReviewStyles } from "./caseCompletePageStyles";
import { auditPillDisplay, getCaseCompleteHeader, reviewPillDisplay } from "./caseCompletePageUtils";
import { PillSection } from "./PageSections/PillSection";
import { OutcomeSection } from "./PageSections/OutcomeSection";

const CaseCompletePage = () => {
  const classes = useClinicalReviewStyles();
  const { caseId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [cohereIds, setCohereIds] = useState<Array<string>>([]);

  const { activeQueue, queueView } = useContext(UserContext);
  const qs = useParsedQueryString();
  const patientId = qs["patientId"];
  const reviewServiceRequestId = qs["reviewServiceRequestId"];
  const origin = qs["origin"];
  const followUpReview = qs["followUpReview"];
  useSyncQueryParamsToPath({ ...qs, queueId: activeQueue?.id });

  const {
    data: serviceCase,
    loading: serviceCaseLoading,
    error: getServiceCaseError,
  } = useGetServiceCase({ id: caseId ? caseId : "" });

  const { data: lettersExpansionConfig } = useGetLettersExpansionConfigurationByPayer(
    convertHealthPlanEnumToName(serviceCase?.healthPlan || "")
  );

  const {
    loading: nextServiceCaseLoadingFromQueue,
    error: getNextServiceCaseErrorFromQueue,
    refetch: getNextServiceCaseFromQueue,
  } = useGetNextCaseForQueue({
    queueId: "",
    lazy: true,
  });

  const fetchCase = useCallback(async () => {
    const serviceCaseFromQueue = await getNextServiceCaseFromQueue({
      pathParams: { queueId: activeQueue?.id },
      queryParams: { timeStamp: new Date().toISOString() },
    });

    if (serviceCaseFromQueue) {
      startNextCase(
        () => {},
        serviceCaseFromQueue,
        enqueueSnackbar,
        true,
        false,
        activeQueue?.id,
        activeQueue?.routeConfigByCaseType
      );
    }
  }, [activeQueue?.id, enqueueSnackbar, getNextServiceCaseFromQueue, activeQueue?.routeConfigByCaseType]);

  const startConcurrentReview = useCallback(() => {
    const continuationId = serviceCase?.outcome?.continuationId;
    window.location.assign(
      `${authorizeBaseUrl()}/${continuationId}/review?createNewNurseReview&caseId=${caseId}&queueId=${
        activeQueue?.id
      }&origin=${origin}`
    );
  }, [serviceCase?.outcome?.continuationId, caseId, activeQueue?.id, origin]);

  const isClinicalReviewCase = ![
    "FAX_INTAKE",
    "LETTER_WRITING",
    "MISSING_INFORMATION",
    "NEW_HIRE_AUDIT_REVIEW",
    "FAX_AUDIT_REVIEW",
    "FOLLOW_UP",
  ].includes(serviceCase?.caseType || "");
  const isP2PReviewCase = serviceCase?.caseType === "P2P_REVIEW";
  const isOutreachCase = serviceCase?.caseType === "MISSING_INFORMATION";
  const isFaxIntakeCase = serviceCase?.caseType === "FAX_INTAKE";
  const isFaxAuditReviewCase = serviceCase?.caseType === "FAX_AUDIT_REVIEW";

  const isFollowUpCaseCompleted = serviceCase?.caseType === "FOLLOW_UP" && followUpReview === "Completed";
  const canContinueToClinicalReview =
    serviceCase?.healthPlan === "GEISINGER" &&
    isFaxIntakeCase &&
    (serviceCase.outcome?.outcome === "NEW_SR" || serviceCase.outcome?.outcome === "FAX_ATTACHED");

  const serviceCaseCohereIds = (serviceCase: ServiceCase | null) => {
    const serviceRequestCohereIds = serviceCase?.outcome?.serviceRequests?.map((item) => item.cohereId || "- -");
    const referralRequestCohereIds = [serviceCase?.outcome?.referralRequest?.cohereId || "- -"];

    if (serviceCase?.outcome?.outcome === "NEW_SR" || "NEW_CONTINUATION_SR" || "EDIT_EXISTING_SR") {
      return serviceRequestCohereIds;
    }

    if (serviceCase?.outcome?.outcome === "NEW_REFERRAL") {
      return referralRequestCohereIds;
    }

    return [];
  };

  useEffect(() => {
    const cohereIds = serviceCaseCohereIds(serviceCase);

    setCohereIds(cohereIds || []);
  }, [serviceCase]);

  useEffect(() => {
    if (getServiceCaseError) {
      enqueueSnackbar("Error fetching case", {
        variant: "error",
        preventDuplicate: true,
      });
    }
    if (getNextServiceCaseErrorFromQueue) {
      enqueueSnackbar("Error fetching next case", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [getServiceCaseError, getNextServiceCaseErrorFromQueue, enqueueSnackbar]);

  return serviceCase ? (
    <Grid container direction="column" justifyContent="flex-start" alignItems="center" height="100vh">
      <LoadingDialog open={serviceCaseLoading || nextServiceCaseLoadingFromQueue} headingText="Loading" />
      <Grid container item xs={8} direction="column" justifyContent="center" alignItems="center">
        <Grid item>
          <H1 className={classes.requestSubmittedGridPadding}>
            {getCaseCompleteHeader(
              serviceCase?.outcome?.outcome,
              isClinicalReviewCase,
              serviceCase?.caseType,
              isFollowUpCaseCompleted
            )}
          </H1>
        </Grid>
        {canContinueToClinicalReview && (
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <ContinueToClinicalReviewBanner />
          </Grid>
        )}
        <Grid container direction="row" justifyContent="center" alignItems="center" style={{ padding: "32px" }}>
          {serviceCase?.caseType !== "FAX_INTAKE" ? (
            <>
              {(isClinicalReviewCase || isOutreachCase || isFaxAuditReviewCase) && (
                <PillSection pillDataDisplay={reviewPillDisplay(serviceCase)} />
              )}
              {(serviceCase?.caseType === "NEW_HIRE_AUDIT_REVIEW" || isFaxAuditReviewCase) && (
                <PillSection pillDataDisplay={auditPillDisplay(serviceCase)} />
              )}
              {isFaxAuditReviewCase && (
                <Box className={classes.outcomeCaseTrackingBox}>
                  <Caption className={classes.caption}>Case ID</Caption>
                  <Body1 className={classes.body1}>{serviceCase?.caseNumber}</Body1>
                </Box>
              )}
              <Box className={classes.outcomeCaseTrackingBox}>
                <Caption className={classes.caption}>Tracking#</Caption>
                <Body1 className={classes.body1}>{serviceCase?.serviceRequest?.cohereId}</Body1>
              </Box>
            </>
          ) : (
            <OutcomeSection serviceCase={serviceCase} cohereIds={cohereIds} />
          )}
        </Grid>

        {isP2PReviewCase ? (
          <Box>
            <SecondaryButton
              className={classes.secondaryButton}
              onClick={() => {
                window.location.assign(
                  `${authorizeBaseUrl()}/patient_summary/${patientId}?reviewServiceRequestId=${reviewServiceRequestId}`
                );
              }}
            >
              Back to patient summary
            </SecondaryButton>
            <PrimaryButton className={classes.primaryButton} to={routes.DASHBOARD} component={Link}>
              Back to My Work
            </PrimaryButton>
          </Box>
        ) : (
          <Box>
            <SecondaryButton className={classes.secondaryButton} to={routes.DASHBOARD} component={Link}>
              Back to My Work
            </SecondaryButton>
            {lettersExpansionConfig?.adhocLetter &&
              (serviceCase?.caseType === "RN_REVIEW" || isFollowUpCaseCompleted) && (
                <SecondaryButton
                  className={classes.secondaryButton}
                  onClick={() => {
                    window.open(
                      `${authorizeBaseUrl()}/letter/${serviceCase?.serviceRequest?.serviceRequestId}?caseId=${
                        serviceCase?.id
                      }`
                    );
                  }}
                >
                  Send a letter
                </SecondaryButton>
              )}
            {serviceCase?.caseType === "FOLLOW_UP" && serviceCase?.outcome?.continuationInitiated && !followUpReview ? (
              <PrimaryButton className={classes.primaryButtonFollowUp} onClick={startConcurrentReview}>
                Start concurrent review
              </PrimaryButton>
            ) : (
              <PrimaryButton className={classes.primaryButton} onClick={fetchCase}>
                Start next case
              </PrimaryButton>
            )}
          </Box>
        )}
      </Grid>
      <Grid container item xs={2} direction="column" justifyContent="space-between" alignItems="center">
        {!isP2PReviewCase && (
          <>
            <Divider className={classes.divider} variant="fullWidth" />
            <Grid justifyContent="center" alignItems="center">
              <DashboardMetrics queueView={queueView} activeQueueId={activeQueue?.id} />
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  ) : null;
};

export default CaseCompletePage;
